import {Placement, useAnimatedPopper} from 'platform/components';
import {Box, Icon, Show} from 'platform/foundation';
import styled from 'styled-components';

import {SourcingVehicleResponseBody} from '@dms/api/core';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {TestIdProps} from 'shared/web';

import {PRICE_CHANGE_DETAIL_WIDTH} from '../../constants/PRICE_CHANGE_DETAIL_WIDTH';
import {PriceChangesDetail} from './components/PriceChangesDetail';
import {getFirstScrapedPrice} from './utils/getFirstScrapedPrice';

interface PriceChangesPopperProps extends TestIdProps {
  vehicle: SourcingVehicleResponseBody;
  currentPrice: number;
  currency: string;
  popperPlacement?: Placement;
}

export function PriceChangesPopper(props: PriceChangesPopperProps) {
  const {openPopper, closePopper, referenceRef, popperProps, Popper} = useAnimatedPopper({
    placement: props.popperPlacement,
    usePortal: true,
    autoUpdateOptions: 'default',
  });

  const discount = getFirstScrapedPrice(props.vehicle, props.currentPrice) - props.currentPrice;
  const shouldShowPriceChanges = discount !== 0;

  return (
    <>
      <Show when={shouldShowPriceChanges} whenFeatureEnabled={featureFlags.SOURCING_PRICE_CHANGES}>
        <PriceChangeBox
          ref={referenceRef}
          colorScheme={discount >= 0 ? 'orange' : 'red'}
          onMouseEnter={() => openPopper()}
          onMouseLeave={() => closePopper()}
          onFocus={() => openPopper()}
          onBlur={() => closePopper()}
          onClick={() => openPopper()}
          role="button"
          tabIndex={0}
          aria-label={i18n.t('entity.vehicle.labels.priceChanges')}
        >
          <Icon
            value={discount >= 0 ? 'action/trending_down' : 'action/trending_up'}
            size={3}
            color="general.white"
          />
        </PriceChangeBox>
      </Show>

      <Popper {...popperProps}>
        <Box width={['100vw', PRICE_CHANGE_DETAIL_WIDTH]} padding={[4, 0]}>
          <PriceChangesDetail
            vehicle={props.vehicle}
            currentPrice={props.currentPrice}
            currency={props.currency}
            data-testid={props['data-testid']}
          />
        </Box>
      </Popper>
    </>
  );
}

type PriceChangeBoxProps = {
  colorScheme: 'orange' | 'red';
};

// General Box component doesn't allow to set 2px padding
// eslint-disable-next-line eag/no-css-property
const PriceChangeBox = styled.div<PriceChangeBoxProps>`
  background-color: ${(props) => props.theme.colors.palettes[props.colorScheme][60][100]};
  border-radius: ${(props) => props.theme.radii.xSmall};
  padding: 2px;

  &:hover,
  &:focus-visible {
    background-color: ${(props) => props.theme.colors.palettes[props.colorScheme][70][100]};
  }
`;
